import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {environment} from '../../environments/environment';
import {Ticket} from '../model/Ticket';
import {CarnetDeTemps} from '../model/Fields';
import {CurrentSubject} from './common.service';

@Injectable({
  providedIn: 'root'
})
export class TicketsService {

  protected versions: Map<string, string>;
  protected versionsSubject = new CurrentSubject<Map<string, string>>();
  protected versionsIntegration: Map<string, string>;
  protected versionsIntegrationSubject = new CurrentSubject<Map<string, string>>();
  protected states: Map<string, string>;
  protected statesSubject = new CurrentSubject<Map<string, string>>();
  protected queues: Map<string, string>;
  protected queuesSubject = new CurrentSubject<Map<string, string>>();
  protected products: Map<string, string>;
  protected productsSubject = new CurrentSubject<Map<string, string>>();
  protected services: Map<string, string>;
  protected servicesSubject = new CurrentSubject<Map<string, string>>();
  protected cdt: CarnetDeTemps;
  protected cdtSubject = new CurrentSubject<CarnetDeTemps>();

  protected productsWithQueueCache = new Map<string, Map<string, string>>();
  protected slaWithServiceCache = new Map<string, Map<string, string>>();

  constructor(private http: HttpClient) {
    this.http.get<CarnetDeTemps>(`${environment.backend}/tickets/cdt`).subscribe(data => {
      this.cdt = data;
      this.cdtSubject.next(this.cdt);
      this.cdtSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/versions`).subscribe(data => {
      this.versions = data;
      this.versionsSubject.next(this.versions);
      this.versionsSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/versionsintegration`).subscribe(data => {
      this.versionsIntegration = data;
      this.versionsIntegrationSubject.next(this.versionsIntegration);
      this.versionsIntegrationSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/states`).subscribe(data => {
      this.states = data;
      this.statesSubject.next(this.states);
      this.statesSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/queues`).subscribe(data => {
      this.queues = data;
      this.queuesSubject.next(this.queues);
      this.queuesSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/products`).subscribe(data => {
      this.products = data;
      this.productsSubject.next(this.products);
      this.productsSubject.complete();
    });

    this.http.get<Map<string, string>>(`${environment.backend}/tickets/services`).subscribe(data => {
      this.services = data;
      this.servicesSubject.next(this.services);
      this.servicesSubject.complete();
    });
  }

  getTickets(company: boolean, search: string): Observable<Ticket[]> {
    return this.http.get<Ticket[]>(`${environment.backend}/tickets${company ? '/company' : ''}`, {params: {search}});
  }

  getFullTicket(id: string): Observable<Ticket> {
    return this.http.get<Ticket>(`${environment.backend}/tickets/${id}`);
  }

  getTicketNumber(id: string): Observable<Ticket> {
    return this.http.get<Ticket>(`${environment.backend}/tickets/${id}/number`);
  }

  updateTicket(id: string, response: any): Observable<any> {
    return this.http.post<any>(`${environment.backend}/tickets/${id}`, response);
  }

  getCarnetDeTemps(): Observable<CarnetDeTemps> {
    return this.cdtSubject.asObservable();
  }

  getPossibleVersions(): Observable<Map<string, string>> {
    return this.versionsSubject.asObservable();
  }

  getPossibleVersionsIntegration(): Observable<Map<string, string>> {
    return this.versionsIntegrationSubject.asObservable();
  }

  getStates(): Observable<Map<string, string>> {
    return this.statesSubject.asObservable();
  }

  getServices(): Observable<Map<string, string>> {
    return this.servicesSubject.asObservable();
  }

  getQueues(): Observable<Map<string, string>> {
    return this.queuesSubject.asObservable();
  }

  getProductsWithQueue(id: string): Observable<Map<string, string>> {
    if (!id) {
      return of(new Map<string, string>());
    }
    const obs = new ReplaySubject<Map<string, string>>();
    if (this.productsWithQueueCache.has(id)) {
      obs.next(this.productsWithQueueCache.get(id));
      obs.complete();
    } else {
      this.http.get<Map<string, string>>(`${environment.backend}/tickets/products/${id}`).subscribe(d => {
        this.productsWithQueueCache.set(id, d);
        obs.next(d);
        obs.complete();
      });
    }
    return obs.asObservable();
  }

  getSLAWithService(id: string): Observable<Map<string, string>> {
    if (!id) {
      return of(new Map<string, string>());
    }
    const obs = new ReplaySubject<Map<string, string>>();
    if (this.slaWithServiceCache.has(id)) {
      obs.next(this.slaWithServiceCache.get(id));
      obs.complete();
    } else {
      this.http.get<Map<string, string>>(`${environment.backend}/tickets/sla/${id}`).subscribe(d => {
        this.slaWithServiceCache.set(id, d);
        obs.next(d);
        obs.complete();
      });
    }
    return obs.asObservable();
  }

  createTicket(obj: any): Observable<any> {
    return this.http.post<any>(`${environment.backend}/tickets/`, obj);
  }

  downloadAttachment(ticketid: string, path: string): Observable<Blob> {
    return this.http.get<Blob>(`${environment.backend}/tickets/download/${ticketid}`, {
      params: {path},
      responseType: 'blob' as 'json'
    });
  }
}
